import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formata data para exibição em pt-BR usando UTC
 * Evita problema de fuso horário que subtrai 1 dia
 */
export function formatDateBR(dateStr: string | Date): string {
  const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr
  return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' })
}

/**
 * Parse defensivo de datas vindas de fontes externas (Asaas, webhooks, forms).
 *
 * - Se a string for date-only ("YYYY-MM-DD"), interpreta como midnight em BRT (= 03:00 UTC).
 *   Evita o `new Date("2026-04-25")` que vira UTC midnight e exibe -1 dia em BRT.
 * - Se já contiver hora (ISO completo, com T ou espaço), apenas delega ao construtor padrão.
 *
 * O sistema todo roda em BRT (America/São_Paulo, UTC-3, sem DST desde 2019).
 */
export function parseDateOnlyBRT(dateStr: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    // YYYY-MM-DD → midnight BRT
    return new Date(`${dateStr}T03:00:00.000Z`)
  }
  return new Date(dateStr)
}

const BRT_OFFSET_MS = 3 * 60 * 60 * 1000

/**
 * Converte uma Date pra string YYYY-MM-DD em BRT (UTC-3).
 * BRT não tem horário de verão desde 2019, então offset fixo é seguro.
 *
 * Exemplo: 23:00 BRT do dia 28 (= 02:00 UTC do dia 29) → "2026-04-28".
 */
export function formatYMDInBRT(d: Date): string {
  const brt = new Date(d.getTime() - BRT_OFFSET_MS)
  return brt.toISOString().slice(0, 10)
}

/**
 * Formata valor monetário em BRL
 */
export function formatCurrencyBR(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

/**
 * Escolhe cor de texto (branco ou cinza-escuro) com melhor contraste
 * sobre um background hex (#RRGGBB). Usa luminância perceptual.
 */
export function pickTextColorForBg(bgHex: string | null | undefined): string {
  if (!bgHex || !bgHex.startsWith('#') || bgHex.length < 7) return '#1f2937'
  const c = parseInt(bgHex.slice(1, 7), 16)
  if (Number.isNaN(c)) return '#1f2937'
  const r = (c >> 16) & 0xff
  const g = (c >> 8) & 0xff
  const b = c & 0xff
  const lum = 0.299 * r + 0.587 * g + 0.114 * b
  return lum > 160 ? '#1f2937' : '#ffffff'
}
